/**
 * Bulk barcode generation dialog.
 *
 * Lists products without barcodes. Manager selects which ones to
 * generate for, clicks Generate, and the backend mints EAN-13 codes in
 * bulk. Products with existing barcodes are shown for context but
 * deselected by default (force=true is NOT offered here — regeneration
 * is a per-product confirm action on ProductDetailPage).
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PrintIcon from '@mui/icons-material/Print';
import API from '../../services/api';
import MobileDialog from './MobileDialog';
import BarcodePrintDialog from './BarcodePrintDialog';

const GenerateBarcodeDialog = ({ open, onClose, products = [], onGenerated }) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [generatedProducts, setGeneratedProducts] = useState([]);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  // Reset state when dialog opens. Default selection = products without a barcode.
  useEffect(() => {
    if (open) {
      setError('');
      setSearch('');
      setGeneratedProducts([]);
      setSelected(new Set());
    }
  }, [open, products]);

  // Only show products that DON'T have a barcode
  const withoutBarcode = useMemo(() => (products || []).filter((p) => !p.Barcode), [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return withoutBarcode;
    return withoutBarcode.filter(
      (p) =>
        (p.ProductName || '').toLowerCase().includes(q) ||
        (p.CatalogNumber || '').toLowerCase().includes(q)
    );
  }, [withoutBarcode, search]);

  const toggle = (productId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const selectAllWithoutBarcode = () => {
    setSelected(new Set(filtered.map((p) => p.ProductID)));
  };

  const clearSelection = () => setSelected(new Set());

  const handleGenerate = async () => {
    setError('');
    if (selected.size === 0) {
      setError('בחר לפחות מוצר אחד');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post('/barcode/generate-bulk/', {
        ProductIDs: Array.from(selected),
        force: false,
      });
      // Build the list of freshly barcoded products for the print dialog.
      const byId = Object.fromEntries(products.map((p) => [p.ProductID, p]));
      const updatedRows = (data.updated || []).map((u) => ({
        ...byId[u.ProductID],
        Barcode: u.Barcode,
      }));
      setGeneratedProducts(updatedRows);
      if (onGenerated) onGenerated(data);
    } catch (e) {
      setError(e?.response?.data?.error || 'שגיאה ביצירת ברקודים');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon color="primary" />
          <Typography variant="h6">יצירת ברקודים במאסה</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {generatedProducts.length > 0 && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              <Button
                size="small"
                startIcon={<PrintIcon />}
                onClick={() => setPrintDialogOpen(true)}
              >
                הדפס מדבקות
              </Button>
            }
          >
            נוצרו {generatedProducts.length} ברקודים בהצלחה
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="חיפוש מוצר..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Button size="small" onClick={selectAllWithoutBarcode}>
            בחר הכל
          </Button>
          <Button size="small" onClick={clearSelection}>
            נקה
          </Button>
          <Chip label={`${selected.size} נבחרו`} color={selected.size > 0 ? 'primary' : 'default'} />
        </Box>

        <Divider sx={{ mb: 1 }} />

        <List sx={{ maxHeight: 360, overflow: 'auto' }}>
          {filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              {withoutBarcode.length === 0 ? 'לכל המוצרים כבר יש ברקוד' : 'לא נמצאו מוצרים'}
            </Typography>
          ) : (
            filtered.map((p) => {
              const isSelected = selected.has(p.ProductID);
              return (
                <ListItem
                  key={p.ProductID}
                  button
                  onClick={() => toggle(p.ProductID)}
                  sx={{ borderBottom: '1px solid #f1f5f9' }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Checkbox edge="start" checked={isSelected} />
                  </ListItemIcon>
                  <ListItemText
                    primary={p.ProductName}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {p.CatalogNumber && (
                          <Typography variant="caption">מק״ט: {p.CatalogNumber}</Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>סגור</Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
          onClick={handleGenerate}
          disabled={submitting || selected.size === 0}
          sx={{ direction: 'ltr' }}
        >
          {submitting ? 'יוצר...' : `צור ${selected.size} ברקודים`}
        </Button>
      </DialogActions>

      <BarcodePrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        initialProducts={generatedProducts}
      />
    </MobileDialog>
  );
};

export default GenerateBarcodeDialog;
